"use client";

import { createContext, useContext, useEffect, useState } from "react";

/**
 * La marca de la empresa: el logo y la imagen del tablero.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS COLORES NO ESTAN AQUI, Y ES DELIBERADO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los inyecta el SERVIDOR, en el layout raíz (`marca-servidor.ts`). Aquí
 * estaban antes y producían un parpadeo: la aplicación se pintaba con el
 * naranja de Xtorage y unas décimas después saltaba al color de la empresa.
 *
 * Lo que decide CÓMO se ve la página no puede llegar después de pintarla. El
 * proyecto ya hacía lo mismo con el tema oscuro.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EL LOGO Y LA IMAGEN, POR CONTEXTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Estos sí van por el navegador: son `<img>`, no hay variable de CSS que los
 * sustituya, y un logo que aparece 200 ms tarde no se nota — a diferencia de
 * la interfaz entera cambiando de color.
 *
 * El logo no es un color: es un `<img>`, y no hay variable de CSS que lo
 * sustituya. Va por contexto para que `XtorageLogo` —que ya está en el menú, en
 * el login y en la portada— lo lea sin que cada pantalla tenga que pasárselo.
 *
 * Y `useMarca()` funciona FUERA del proveedor devolviendo `null`: el login y la
 * recuperación de contraseña usan el mismo componente y allí todavía no se sabe
 * de qué empresa es nadie. Sin eso, esas tres pantallas reventarían.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CADA EMPRESA LA SUYA, Y SOLO LA SUYA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Se pide a `/api/settings/branding`, que resuelve la empresa desde la sesión.
 * Un usuario de Éxito recibe el de Éxito y no puede pedir el de Falabella: no
 * hay parámetro que cambiar.
 *
 * Si la empresa no ha tocado nada, no se inyecta nada y se ve como Xtorage.
 */

export type Marca = {
  siteTitle: string | null;
  logoUrl: string | null;
  /** El fondo del tablero de ESTA empresa. La portada publica es de X-Cargo. */
  heroImageUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
};

const MarcaContext = createContext<Marca | null>(null);

/**
 * La marca de la empresa activa, o `null`.
 *
 * `null` significa «no hay empresa» —login, portada, panel de X-Cargo— o «esta
 * empresa no ha configurado nada». Los dos casos llevan a lo mismo: se ve como
 * Xtorage, que es lo correcto.
 */
export function useMarca(): Marca | null {
  return useContext(MarcaContext);
}

/**
 * Lo que el servidor ya dejó en el HTML: logo y nombre.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUE ESTO EXISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sin esto, la primera pintada salía con el logo de Xtorage y saltaba al de la
 * empresa cuando respondía `/api/settings/branding`. Un parpadeo en cada carga
 * de cada pantalla.
 *
 * El layout raíz lee la marca en el servidor y la deja en un `<script
 * type="application/json">`. Leerlo AQUI —en el inicializador del estado, no en
 * un efecto— significa que el primer render ya lo tiene: no hay un instante con
 * el logo equivocado.
 *
 * Solo trae logo y nombre. La imagen de fondo del tablero pesa hasta 800 KB y
 * sigue viniendo de la API: meterla en el HTML de todas las páginas para usarla
 * en una sola sería pagar ese peso en cada navegación.
 */
function marcaDelHtml(): Marca | null {
  if (typeof document === 'undefined') return null;

  const etiqueta = document.getElementById('marca-inicial');
  if (!etiqueta?.textContent) return null;

  try {
    const datos = JSON.parse(etiqueta.textContent) as {
      logoUrl?: string | null;
      siteTitle?: string | null;
    };

    return {
      siteTitle: datos.siteTitle ?? null,
      logoUrl: datos.logoUrl ?? null,
      heroImageUrl: null,
      primaryColor: null,
      accentColor: null,
    };
  } catch {
    // Un JSON roto no puede tumbar la aplicación entera: se sigue como si no
    // hubiera marca y la API la traerá un instante después.
    return null;
  }
}

export function MarcaDeEmpresa({ children }: { children?: React.ReactNode }) {
  // El valor inicial sale del HTML, no de `null`: por eso no hay parpadeo.
  const [marca, setMarca] = useState<Marca | null>(marcaDelHtml);

  useEffect(() => {
    let vigente = true;

    fetch("/api/settings/branding", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (!vigente || !payload?.data) return;

        setMarca({
          siteTitle: payload.data.siteTitle ?? null,
          logoUrl: payload.data.logoUrl ?? null,
          heroImageUrl: payload.data.heroImageUrl ?? null,
          primaryColor: payload.data.primaryColor ?? null,
          accentColor: payload.data.accentColor ?? null,
        });
      })
      .catch(() => {
        // Sin marca se ve como Xtorage. No merece aviso: una empresa que no ha
        // configurado nada es el caso normal.
      });

    return () => {
      vigente = false;
    };
  }, []);

  return <MarcaContext.Provider value={marca}>{children}</MarcaContext.Provider>;
}
